import React, { useState } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ShoppingBag as ProductsIcon,
  ShoppingCart as OrdersIcon,
  People as UsersIcon,
  LocalOffer as CouponsIcon,
  Category as CategoriesIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';

import ProductsManagement from './ProductsManagement';
import OrdersManagement from './OrdersManagement';
import UsersManagement from './UsersManagement';
import CouponsManagement from './CouponsManagement';
import CategoriesManagement from './CategoriesManagement';
import Settings from './Settings';

const drawerWidth = 240;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
  { text: 'Products', icon: <ProductsIcon />, path: '/admin/products' },
  { text: 'Orders', icon: <OrdersIcon />, path: '/admin/orders' },
  { text: 'Users', icon: <UsersIcon />, path: '/admin/users' },
  { text: 'Coupons', icon: <CouponsIcon />, path: '/admin/coupons' },
  { text: 'Categories', icon: <CategoriesIcon />, path: '/admin/categories' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' },
];

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [open, setOpen] = useState(!isMobile);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const renderContent = () => {
    switch (location.pathname) {
      case '/admin':
        return <DashboardOverview />;
      case '/admin/products':
        return <ProductsManagement />;
      case '/admin/orders':
        return <OrdersManagement />;
      case '/admin/users':
        return <UsersManagement />;
      case '/admin/coupons':
        return <CouponsManagement />;
      case '/admin/categories':
        return <CategoriesManagement />;
      case '/admin/settings':
        return <Settings />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
      {/* Drawer */}
      <Paper
        sx={{
          width: open ? drawerWidth : 0,
          flexShrink: 0,
          position: 'fixed',
          top: 64,
          left: 0,
          height: 'calc(100vh - 64px)',
          zIndex: theme.zIndex.drawer,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Box sx={{ overflow: 'auto', mt: 2 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={location.pathname === item.path}
                  sx={{
                    minHeight: 48,
                    px: 2.5,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 3,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <Typography variant="h4" component="h1">
            {menuItems.find((item) => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
        </Box>
        {renderContent()}
      </Box>
    </Box>
  );
};

const DashboardOverview = () => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6} lg={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Total Sales
          </Typography>
          <Typography variant="h4">$24,500</Typography>
          <Typography variant="body2" color="textSecondary">
            +12% from last month
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Total Orders
          </Typography>
          <Typography variant="h4">156</Typography>
          <Typography variant="body2" color="textSecondary">
            +8% from last month
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Total Users
          </Typography>
          <Typography variant="h4">1,245</Typography>
          <Typography variant="body2" color="textSecondary">
            +5% from last month
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6} lg={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Products
          </Typography>
          <Typography variant="h4">89</Typography>
          <Typography variant="body2" color="textSecondary">
            +3% from last month
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Dashboard; 